import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { ActivityIcon, AlertTriangleIcon, ShieldAlertIcon, MapPinIcon, BellRingIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const SCREENSHOTS = ['https://cdn.magicpatterns.com/uploads/wXkee2NNhosYHjvi3hu9tW/image.png', 'https://cdn.magicpatterns.com/uploads/5ZiLoXjmWbfet5YntEV91b/image.png', 'https://cdn.magicpatterns.com/uploads/mRuy8QSAGiRLLs2aP59rrK/image.png'];
const FEATURES = [{
  label: 'مراقبة السرعة الزائدة',
  icon: ActivityIcon
}, {
  label: 'تحليل الفرملة المفاجئة',
  icon: AlertTriangleIcon
}, {
  label: 'اكتشاف الحوادث',
  icon: ShieldAlertIcon
}, {
  label: 'تنبيهات القرب',
  icon: MapPinIcon
}, {
  label: 'إشعارات الامتثال',
  icon: BellRingIcon
}];
export function ValueSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-609">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-610">
        <div className="absolute -top-40 -right-40 w-[60vmax] h-[60vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.06) 0%, transparent 70%)'
      }} data-id="element-611" />
        <div className="absolute -bottom-40 -left-40 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.04) 0%, transparent 70%)'
      }} data-id="element-612" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-613" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.9,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1.5rem,3.5vh,3rem)] px-[clamp(1.75rem,4.5vw,4.5rem)] shrink-0" data-id="element-614">
        <SharjahPoliceEmblem variant="light" data-id="element-615" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.5rem,5vh,3.5rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-616" />
      </motion.div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-20 flex-1 flex flex-col lg:flex-row items-center justify-between gap-12 px-[clamp(3rem,6vw,8rem)] pb-[clamp(2rem,6vh,4rem)]" data-id="element-617">
        {/* RIGHT SIDE: Text Content (First in RTL) */}
        <div className="flex-1 flex flex-col justify-center max-w-2xl" data-id="element-618">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.2
        }} className="font-arabic-display font-black text-right mb-4 leading-[1.15]" style={{
          color: '#0B1328',
          fontSize: 'clamp(2.2rem, 4vw, 3.5rem)'
        }} data-id="element-619">
            المراقبة الاستباقية لسلوك السائقين
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.3
        }} className="font-arabic-body font-bold text-right mb-6" style={{
          color: '#2FA99C',
          fontSize: 'clamp(1.1rem, 1.5vw, 1.35rem)'
        }} data-id="element-620">
            مدعوم بالذكاء الاصطناعي لتحليل الأنماط والتنبؤ بالمخاطر
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.4
        }} className="font-arabic-body text-right mb-12 leading-relaxed" style={{
          color: '#6E7A8F',
          fontSize: 'clamp(1rem, 1.2vw, 1.15rem)'
        }} data-id="element-621">
            نظام متقدم يراقب سلوك القيادة في الوقت الفعلي، يحلل البيانات بدقة
            عالية، ويتخذ إجراءات استباقية لضمان أعلى مستويات السلامة والامتثال.
          </motion.p>

          {/* Features List */}
          <div className="flex flex-col gap-5" data-id="element-622">
            {FEATURES.map((feat, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={base ?? {
            duration: 0.6,
            delay: 0.5 + i * 0.1
          }} className="flex items-center gap-4 bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-white/40 shadow-sm w-fit pr-5 pl-8" data-id="element-623">
                <div className="w-10 h-10 rounded-xl bg-[#2FA99C]/10 flex items-center justify-center shrink-0" data-id="element-624">
                  <feat.icon className="w-5 h-5 text-[#2FA99C]" data-id="element-625" />
                </div>
                <span className="font-arabic-body text-[#0B1328] font-bold text-lg" data-id="element-626">
                  {feat.label}
                </span>
              </motion.div>)}
          </div>
        </div>

        {/* LEFT SIDE: Floating Phones (Second in RTL) */}
        <div className="flex-1 relative h-full min-h-[500px] flex items-center justify-center perspective-[1200px]" data-id="element-627">
          {/* Phone 1 (Back Left) */}
          <motion.div initial={{
          opacity: 0,
          x: -50,
          y: 20,
          rotateY: 0,
          rotateZ: 0
        }} animate={{
          opacity: 1,
          x: -170,
          y: [-30, -40, -30],
          rotateY: 0,
          rotateZ: 0
        }} transition={{
          opacity: {
            duration: 1,
            delay: 0.6
          },
          x: {
            duration: 1,
            delay: 0.6,
            type: 'spring'
          },
          y: {
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.2
          }
        }} className="absolute z-10 w-[250px] aspect-[9/19.5] rounded-[2.5rem] overflow-hidden shadow-2xl border-[6px] border-white/80 bg-white" data-id="element-628">
            <img src={SCREENSHOTS[2]} alt="App Screen 3" className="w-full h-full object-cover object-top" data-id="element-629" />
          </motion.div>

          {/* Phone 2 (Back Right) */}
          <motion.div initial={{
          opacity: 0,
          x: 50,
          y: 40,
          rotateY: 0,
          rotateZ: 0
        }} animate={{
          opacity: 1,
          x: 170,
          y: [-10, -20, -10],
          rotateY: 0,
          rotateZ: 0
        }} transition={{
          opacity: {
            duration: 1,
            delay: 0.7
          },
          x: {
            duration: 1,
            delay: 0.7,
            type: 'spring'
          },
          y: {
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }
        }} className="absolute z-20 w-[260px] aspect-[9/19.5] rounded-[2.5rem] overflow-hidden shadow-2xl border-[6px] border-white/90 bg-white" data-id="element-630">
            <img src={SCREENSHOTS[1]} alt="App Screen 2" className="w-full h-full object-cover object-top" data-id="element-631" />
          </motion.div>

          {/* Phone 3 (Front Center) */}
          <motion.div initial={{
          opacity: 0,
          y: 60,
          scale: 0.9
        }} animate={{
          opacity: 1,
          y: [10, 0, 10],
          scale: 1
        }} transition={{
          opacity: {
            duration: 1,
            delay: 0.8
          },
          scale: {
            duration: 1,
            delay: 0.8,
            type: 'spring'
          },
          y: {
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} className="absolute z-30 w-[280px] aspect-[9/19.5] rounded-[2.5rem] overflow-hidden shadow-[0_30px_60px_rgba(11,19,40,0.2)] border-[8px] border-white bg-white" data-id="element-632">
            <img src={SCREENSHOTS[0]} alt="App Screen 1" className="w-full h-full object-cover object-top" data-id="element-633" />
          </motion.div>
        </div>
      </div>
    </section>;
}